import { useState } from "react";
import { ChevronRight, Printer } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ProductImageButton } from "@/components/ProductImageThumb";
import { getEffectiveFnsku, getPrintQuantity, printFnskuLabels } from "@/lib/fnskuPrint";
import { storeTotals } from "@/lib/boxing";

const STEP_BUTTON = "flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-base font-bold leading-none text-slate-700 transition-colors hover:bg-slate-100";

function PlanRow({ item, flag, canPrint, onMessage }) {
  const { t } = useLang();
  const [draft, setDraft] = useState("1");
  const fnsku = getEffectiveFnsku(item);
  const quantity = getPrintQuantity(draft, 1);
  const done = item.remaining <= 0;
  const revised = item.revised_from != null || !!item.note || item.over > 0; // re-counted, noted, or boxed past the plan
  const percent = item.planned > 0 ? Math.min(100, Math.round((item.boxed / item.planned) * 100)) : 0;
  const flagText = flag && [
    flag.damaged > 0 && `${t("disc.damaged")} ${flag.damaged}`,
    flag.missing > 0 && `${t("disc.missing")} ${flag.missing}`,
  ].filter(Boolean).join(" · ");

  const print = async () => {
    try {
      await printFnskuLabels([{ fnsku, title: `New - ${item.product_name}`, quantity }]);
      onMessage("success", t("recv.printing", { count: quantity }));
    } catch (err) {
      console.error(err);
      onMessage("error", t("recv.print_failed"));
    }
  };

  return (
    <div className={`grid items-center gap-x-3.5 gap-y-1 px-3 py-2 sm:grid-cols-[1fr_150px_auto] ${revised ? "bg-amber-50" : ""}`}>
      <div className="flex min-w-0 items-center gap-2.5">
        <ProductImageButton asin={item.asin} name={item.product_name} fnsku={fnsku} />
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <span className="truncate">{item.product_name}</span>
            {flagText && <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-px text-[11px] font-bold text-amber-700">{flagText}</span>}
          </p>
          <p className="truncate font-mono text-[11px] text-slate-400">{fnsku || t("recv.fnsku_missing")}</p>
          {(item.revised_from != null || !!item.note) && (
            <p className="text-xs font-medium text-amber-800">
              <span className="mr-1.5 rounded bg-amber-200 px-1.5 py-px font-bold">
                {item.revised_from != null ? t("planning.revised_was", { count: item.revised_from }) : t("planning.revised")}
              </span>
              {item.note}
            </p>
          )}
          {item.over > 0 && (
            <p className="text-xs font-medium text-amber-800">
              <span className="mr-1.5 rounded bg-amber-200 px-1.5 py-px font-bold">{t("recv.over_badge", { count: item.over })}</span>
              {item.over_reasons.map(r => `${t(`recv.over_r.${r.reason}`)}${r.note ? ` · ${r.note}` : ""}`).join("; ")}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs font-bold">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
          <i className={`block h-full ${done ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${percent}%` }} />
        </div>
        <span className="min-w-10 text-right text-slate-600">{item.boxed}/{item.planned}</span>
      </div>
      {canPrint ? (
        <div className="flex items-center gap-1.5">
          <button type="button" className={STEP_BUTTON} disabled={quantity <= 1}
            onClick={() => setDraft(String(quantity - 1))} aria-label={t("recv.print_decrease", { name: item.product_name })}>−</button>
          <input type="text" inputMode="numeric" pattern="[0-9]*" value={draft}
            onChange={e => /^\d*$/.test(e.target.value) && setDraft(e.target.value)}
            onFocus={e => e.target.select()} onClick={e => e.target.select()}
            onBlur={() => setDraft(String(quantity))}
            className="h-8 w-14 rounded-lg border border-slate-200 bg-white text-center text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={t("recv.print_quantity", { name: item.product_name })} />
          <button type="button" className={STEP_BUTTON}
            onClick={() => setDraft(String(quantity + 1))} aria-label={t("recv.print_increase", { name: item.product_name })}>+</button>
          <button type="button" className={`${STEP_BUTTON} hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-700`}
            onClick={print} disabled={!fnsku}
            title={fnsku ? t("recv.print_fnsku") : t("recv.no_fnsku")} aria-label={t("recv.print_fnsku_for", { name: item.product_name })}>
            <Printer className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : <span />}
    </div>
  );
}

// Collapsible plan of one store: what was planned, what is boxed, and FNSKU printing per product.
export default function StorePlan({ store, flags, canPrint, onMessage }) {
  const { t } = useLang();
  const { planned, boxed, left } = storeTotals(store);

  return (
    <details open={left > 0} className="group">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold text-slate-800 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-90" />
        {t("recv.store_plan")}
        <span className="font-normal text-slate-400">{t("recv.plan_summary", { types: store.items.length, boxed, planned })}</span>
      </summary>
      <div className="mx-4 mb-2 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
        {store.items.map(item => (
          <PlanRow key={item.asin} item={item} flag={flags[item.asin]} canPrint={canPrint} onMessage={onMessage} />
        ))}
      </div>
    </details>
  );
}
